import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, MessageCircle, Save, Target } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { CampoImpostazione } from './CampoImpostazione';
import { useSettingsStore } from '../../store/useSettingsStore';
import { useNotifications } from '../../store/useStore';
import {
    GIORNI_RICONTATTO_MAX,
    GIORNI_RICONTATTO_MIN,
    SEGNAPOSTI_WHATSAPP,
    componiMessaggioWhatsapp,
    type LeadsSettings
} from '../../types/settings.types';

/**
 * Le due cose della sezione Lead che prima erano scritte nel codice:
 * dopo quanti giorni un lead va ricontattato, e cosa c'e' gia' scritto nella
 * chat quando si preme WhatsApp su un lead.
 */
export function LeadSettingsCard() {
    const { lead, salvaLead } = useSettingsStore();
    const { addNotification } = useNotifications();
    const [bozza, setBozza] = useState<LeadsSettings>(lead);
    const [salvataggio, setSalvataggio] = useState(false);

    useEffect(() => setBozza(lead), [lead]);

    const erroreGiorni = useMemo(() => {
        const giorni = bozza.giorniRicontatto;
        if (!Number.isFinite(giorni) || giorni < GIORNI_RICONTATTO_MIN || giorni > GIORNI_RICONTATTO_MAX) {
            return `Scegli un numero fra ${GIORNI_RICONTATTO_MIN} e ${GIORNI_RICONTATTO_MAX}.`;
        }
        return undefined;
    }, [bozza.giorniRicontatto]);

    const modificato = useMemo(
        () => bozza.giorniRicontatto !== lead.giorniRicontatto || bozza.messaggioWhatsapp !== lead.messaggioWhatsapp,
        [bozza, lead]
    );

    // Anteprima su un lead finto, cosi' si vede subito cosa legge il cliente.
    const anteprima = useMemo(
        () => componiMessaggioWhatsapp(bozza.messaggioWhatsapp, { nome: 'Marco', azienda: 'Rossi Srl' }),
        [bozza.messaggioWhatsapp]
    );

    const handleSalva = async () => {
        if (erroreGiorni) return;
        setSalvataggio(true);
        try {
            await salvaLead({ ...bozza, giorniRicontatto: Math.round(bozza.giorniRicontatto) });
            addNotification({ type: 'success', title: 'Impostazioni lead salvate' });
        } catch (error) {
            console.error('Errore salvataggio impostazioni lead:', error);
            addNotification({
                type: 'error',
                title: 'Non salvato',
                message: 'Il database ha rifiutato il salvataggio. Riprova.'
            });
        } finally {
            setSalvataggio(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-amber-600" />
                    <CardTitle>Lead</CardTitle>
                </div>
                <CardDescription>
                    Quando un lead va ricontattato e cosa scrive il tasto WhatsApp.
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                    <CampoImpostazione
                        etichetta={"Giorni prima dell’avviso “da ricontattare”"}
                        tipo="number"
                        valore={String(bozza.giorniRicontatto)}
                        onChange={(v) => setBozza((p) => ({ ...p, giorniRicontatto: Number(v) }))}
                        errore={erroreGiorni}
                        aiuto={
                            erroreGiorni
                                ? undefined
                                : `Un lead in lavorazione diventa giallo dopo ${bozza.giorniRicontatto} giorni senza contatto, rosso dopo ${bozza.giorniRicontatto * 2}.`
                        }
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="messaggio-whatsapp" className="text-sm font-medium text-gray-700">
                        Messaggio WhatsApp predefinito
                    </Label>
                    <Textarea
                        id="messaggio-whatsapp"
                        rows={3}
                        value={bozza.messaggioWhatsapp}
                        onChange={(e) => setBozza((p) => ({ ...p, messaggioWhatsapp: e.target.value }))}
                        placeholder="Lascia vuoto per aprire WhatsApp senza testo già scritto"
                    />
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span>Segnaposto:</span>
                        {SEGNAPOSTI_WHATSAPP.map((s) => (
                            <button
                                key={s.chiave}
                                type="button"
                                title={`Inserisci ${s.descrizione}`}
                                onClick={() =>
                                    setBozza((p) => ({
                                        ...p,
                                        messaggioWhatsapp: `${p.messaggioWhatsapp}${p.messaggioWhatsapp.endsWith(' ') || !p.messaggioWhatsapp ? '' : ' '}${s.chiave}`
                                    }))
                                }
                                className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-mono text-emerald-700 transition-colors hover:bg-emerald-100"
                            >
                                {s.chiave}
                            </button>
                        ))}
                        <span>&mdash; vengono sostituiti coi dati di quel lead.</span>
                    </div>

                    {bozza.messaggioWhatsapp.trim() && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
                                <MessageCircle className="h-3.5 w-3.5" />
                                Anteprima su un lead di esempio
                            </p>
                            <p className="text-sm text-emerald-900">{anteprima}</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button variant="outline" onClick={() => setBozza(lead)} disabled={!modificato || salvataggio}>
                        Annulla modifiche
                    </Button>
                    <Button onClick={handleSalva} disabled={!modificato || salvataggio || Boolean(erroreGiorni)}>
                        {salvataggio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Salva
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export default LeadSettingsCard;
